/**
 * Client workspace shell — renders the per-client tab strip above the
 * routed outlet.
 *
 * Tab visibility is gated by THREE independent inputs:
 *   1. User permission (UX hint; RLS is the load-bearing security gate).
 *   2. Active client package capability set (Task 051) — which surfaces
 *      this client has actually contracted for.
 *   3. The "foundation" tabs are always visible regardless of package
 *      (overview, onboarding, strategy, services, reports).
 *
 * Agency staff with `client.manage` (or any agency-side role) get a
 * labelled "معاينة الوكالة" badge on tabs the package does NOT unlock,
 * so they can preview the underlying surface without misleading the
 * client about what they've contracted for. Client-side users never
 * see locked execution tabs at all.
 *
 * The capability set is exposed to child routes via the React Router
 * `<Outlet context={...} />` channel so pages like Overview can render
 * a "no active package" banner without re-fetching the assignment.
 *
 * CRM is intentionally absent from this tab strip. CRM is an
 * agency-internal surface and is never client-facing per Task 051 §3.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { isApiError } from '../api/client';
import { listClientPackageAssignments } from '../api/endpoints';
import type { ClientPackageAssignmentRow, SystemCapabilityKey } from '../api/types';
import { ErrorState, LoadingState, NotFoundState } from '../components/states';
import { useClientScope } from '../context/ClientScopeProvider';
import { useMeContext } from '../context/MeContextProvider';
import {
  capabilityLabelAr,
  hasAnyPermission,
  hasClientAssignment,
  hasRoleCategory,
  isClientOnly,
  isLeadership,
  PERM,
} from '../permissions';

/**
 * Slug → capability key for execution tabs. Used by the outlet guard
 * below to decide whether to show a loading skeleton, the locked
 * state, or the actual page content. Foundation slugs (overview,
 * onboarding, strategy, services, reports) are intentionally absent.
 */
const CAPABILITY_GATED_ROUTES: Readonly<Record<string, SystemCapabilityKey>> = {
  content: 'content',
  publishing: 'publishing',
  'paid-media': 'paid_media',
  creative: 'creative',
  influencers: 'influencers',
};

const INFLUENCER_SURFACES_ENABLED = false;

interface TabDef {
  readonly slug: string;
  readonly label: string;
  readonly visible: boolean;
  readonly previewOnly?: boolean;
}

type CapabilityState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly activeAssignment: ClientPackageAssignmentRow | null;
      readonly capabilities: ReadonlySet<SystemCapabilityKey>;
      /**
       * True only when the assignment RPC actually returned data. The catch
       * path below degrades RLS-denied / not-found to a `ready` state with a
       * null assignment, so `status === 'ready'` alone does NOT prove the
       * lookup succeeded — consumers that need "definitely no active package"
       * (vs "lookup blocked") must read this flag.
       */
      readonly assignmentLookupConfirmed: boolean;
    }
  | { readonly status: 'error'; readonly error: unknown };

/**
 * Shape passed via `<Outlet context>` to all client workspace pages.
 * Routes consume it via `useClientWorkspaceContext()` (see below).
 */
export interface ClientWorkspaceOutletContext {
  readonly clientId: string;
  readonly capabilityStatus: 'loading' | 'ready' | 'error';
  readonly hasActivePackage: boolean;
  readonly capabilities: ReadonlySet<SystemCapabilityKey>;
  readonly isAgencyActor: boolean;
  readonly activeAssignment: ClientPackageAssignmentRow | null;
  readonly refreshPackageAssignments: () => void;
  /**
   * True only when the package-assignment lookup actually succeeded. The layout
   * degrades RLS-denied / not-found to `capabilityStatus: 'ready'` with a null
   * assignment, so this distinguishes "definitely no active package" (confirmed)
   * from "lookup was blocked" (not confirmed) — needed before telling the user
   * there is no active package.
   */
  readonly assignmentLookupConfirmed: boolean;
}

export function ClientWorkspaceLayout() {
  const { clientId } = useParams();
  // Task hide-archived-clients — also read scope status + error so we
  // can distinguish:
  //   - 'loading' / 'idle' → render the existing tabs with placeholder
  //     copy (the layout's own capability fetch will paint progress)
  //   - 'ready' && !client → archived or unknown → NotFoundState
  //   - 'error' → ErrorState with the scope error (covers RLS / network
  //     failures of the underlying list-clients call). Without this
  //     branch, a scope-load failure would silently render the workspace
  //     shell with no client metadata and stale tabs.
  const { clients, status: scopeStatus, error: scopeError } = useClientScope();
  const { me } = useMeContext();
  const [capabilityState, setCapabilityState] = useState<CapabilityState>({ status: 'loading' });
  const [capabilityReloadKey, setCapabilityReloadKey] = useState(0);

  const refreshPackageAssignments = useCallback(() => {
    setCapabilityReloadKey((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!clientId) return;
    const ctrl = new AbortController();
    setCapabilityState({ status: 'loading' });
    listClientPackageAssignments({ clientId, signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        const active = data.assignments.find((a) => a.status === 'active') ?? null;
        const capabilities: ReadonlySet<SystemCapabilityKey> = active
          ? collectCapabilities(active)
          : new Set();
        setCapabilityState({
          status: 'ready',
          activeAssignment: active,
          capabilities,
          assignmentLookupConfirmed: true,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // RLS may legitimately block this call for some actors (e.g. a
        // client_admin without billing.read). Treat that as "no
        // capability data" — show foundation tabs only and degrade
        // gracefully. Surface non-RLS errors so we don't hide bugs.
        if (isApiError(err) && (err.code === 'permission_denied' || err.code === 'not_found')) {
          setCapabilityState({
            status: 'ready',
            activeAssignment: null,
            capabilities: new Set(),
            // Lookup was RLS-denied / not-found — NOT a confirmed "no package".
            assignmentLookupConfirmed: false,
          });
          return;
        }
        setCapabilityState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, capabilityReloadKey]);

  if (!clientId || !me) return null;
  const client = clients.find((c) => c.id === clientId);

  // Task hide-archived-clients — explicit error branch BEFORE the
  // ready+missing check. If the underlying list-clients call failed
  // (RLS denied, network down, EF crashed), `scopeStatus === 'error'`
  // means we have NO reliable scope; rendering the workspace shell
  // would mislead the operator about what client they're acting on.
  if (scopeStatus === 'error') {
    return (
      <ErrorState
        title="تعذر تحميل سياق العميل"
        description="حدث خطأ أثناء تحميل قائمة العملاء. حاول تحديث الصفحة أو العودة إلى قائمة العملاء."
        error={scopeError}
      />
    );
  }

  // Task hide-archived-clients — when the client scope is fully loaded
  // and the URL clientId is NOT in the actor's visible set, surface an
  // explicit unavailable state. Two cases produce this:
  //   - Cross-tenant probe / typo / deleted clientId → 404-equivalent.
  //   - Archived client → list-clients excludes archived from the
  //     leadership scope by default, and `_shared/context.ts` filters
  //     archived rows out of non-leadership assignment lists. Either
  //     way, archived clients should not surface a workspace.
  // The Arabic copy mentions أرشفته so the operator has a hint about
  // what likely happened. A future archive-management UI (out of
  // current scope) can route here with a status query param to render
  // a more specific banner.
  if (scopeStatus === 'ready' && !client) {
    return (
      <NotFoundState
        title="هذا العميل غير متاح"
        description="قد يكون العميل مؤرشفاً أو غير موجود في نطاقك. ارجع إلى قائمة العملاء."
        homePath="/app/clients"
      />
    );
  }

  const perms = me.permissions;
  const isAgencyActor = !me.system_admin && hasRoleCategory(me, 'agency');

  const capLoaded = capabilityState.status === 'ready';
  const capabilities: ReadonlySet<SystemCapabilityKey> =
    capabilityState.status === 'ready' ? capabilityState.capabilities : new Set();
  const hasActivePackage =
    capabilityState.status === 'ready' && capabilityState.activeAssignment !== null;

  /**
   * Tab visibility resolver.
   *
   *   - foundation = `true` → always visible if user perm allows.
   *   - capability key → require capability OR (agency actor with
   *     base perm → preview-only mode).
   *
   * Loading state: foundation tabs render immediately; capability-
   * gated tabs are hidden until the assignment query resolves to
   * avoid flickering tabs the package doesn't actually unlock.
   */
  const visibilityFor = (
    perm: ReadonlyArray<string>,
    capKey: SystemCapabilityKey | null,
  ): { visible: boolean; previewOnly: boolean } => {
    if (perm.length > 0 && !hasAnyPermission(perms, ...perm)) {
      return { visible: false, previewOnly: false };
    }
    if (capKey === null) return { visible: true, previewOnly: false };
    if (!capLoaded) return { visible: false, previewOnly: false };
    if (capabilities.has(capKey)) return { visible: true, previewOnly: false };
    if (isAgencyActor) return { visible: true, previewOnly: true };
    return { visible: false, previewOnly: false };
  };

  // Foundation tabs are gated only by user permission (or always-on
  // for the truly universal ones). Execution tabs are gated by both
  // user permission AND active package capability. CRM intentionally
  // absent — agency-only surface.
  const onboardingVis = visibilityFor([], null);
  // Task 292C3A — either the canonical `strategy.read` (owner / MM /
  // account_manager + client roles with the seeded key) OR the
  // writer-only `strategy.read.assigned_active` gets the Strategy tab.
  // The RLS SELECT policies enforce the actual scope; the tab visibility
  // is UI-only. StrategyPage's write / publish / AI buttons stay gated
  // on their own permissions (STRATEGY_VERSION_PUBLISH / AI_RUN /
  // STRATEGY_CREATE / STRATEGY_RULE_REVIEW), none of which writers hold.
  const strategyVis = visibilityFor([PERM.STRATEGY_READ, PERM.STRATEGY_READ_ASSIGNED_ACTIVE], null); // foundation
  const reportsVis = visibilityFor([PERM.REPORT_READ], null); // foundation (see §3)
  const servicesVis = visibilityFor([PERM.BILLING_READ, PERM.CONTRACT_READ], null); // foundation
  // Task 303-A — the agency-client contract. FOUNDATION tab (no package
  // capability): a contract exists before any package is delivered, and
  // an archived client still reads it. Gated on `contract.read`, which
  // owner / marketing_manager / account_manager / finance_manager and
  // client_admin all hold; the endpoints decide who sees which
  // projection and who may download the PDF.
  const contractVis = visibilityFor([PERM.CONTRACT_READ], null);
  const contentVis = visibilityFor([PERM.CONTENT_READ], 'content');
  const publishingVis = visibilityFor([PERM.PUBLISH_CONNECTION_READ], 'publishing');
  // Task 144 — client-only users assigned to this client don't carry
  // `paid_media.*.read` permissions (the seeded permission set is
  // agency-centric) but DO need to see the paid-media tab when the
  // package unlocks `paid_media` so they can reach the Task 141 IC-3
  // Meta Ads connect CTA. Relax the permission half of the visibility
  // gate for client-only-assigned users; the capability gate on
  // `'paid_media'` stays load-bearing and continues to hide the tab
  // when the package is locked. Agency users keep the existing
  // permission-first, capability-second behaviour (including the
  // preview-only badge when an agency previewer has perm but the
  // package is locked). Relaxation is localized to paid-media — no
  // other capability-gated tab (content / publishing / creative /
  // influencers) is touched by this hotfix.
  //
  // `capLoaded && capabilities.has('paid_media')` is false while the
  // assignment query is still in flight and stays false when the
  // package is locked — both cases collapse to a hidden tab for the
  // client-only branch, which is the same loading/hidden posture the
  // generic `visibilityFor` uses for capability-gated tabs.
  const isClientOnlyAssigned =
    !me.system_admin && isClientOnly(me) && hasClientAssignment(me, clientId);
  const clientOnlyPaidMediaVisible =
    isClientOnlyAssigned && capLoaded && capabilities.has('paid_media');
  const paidMediaVis: { visible: boolean; previewOnly: boolean } = isClientOnlyAssigned
    ? { visible: clientOnlyPaidMediaVisible, previewOnly: false }
    : visibilityFor([PERM.PAID_MEDIA_ACCOUNT_READ, PERM.PAID_MEDIA_CAMPAIGN_READ], 'paid_media');
  const creativeVis = visibilityFor([PERM.CREATIVE_INTELLIGENCE_READ], 'creative');
  // Task 223 — Creative Intelligence client-workspace tab. Permission-gated on
  // CREATIVE_INTELLIGENCE_READ only (NOT capability-gated): it is a read
  // analytics surface, available wherever the actor can read creative
  // intelligence, independent of the client's contracted package.
  const creativeIntelligenceVis = visibilityFor([PERM.CREATIVE_INTELLIGENCE_READ], null);
  const influencersVis = INFLUENCER_SURFACES_ENABLED
    ? visibilityFor([PERM.INFLUENCER_CATALOG_READ], 'influencers')
    : { visible: false, previewOnly: false };
  // Task 109 — client team management. Leadership-only: the
  // `client_assignments_select` RLS policy only returns the full roster
  // to owner / marketing_manager / account_manager, and requirement #1
  // names only owner / marketing_manager as team managers. A
  // non-leadership employee would see a misleading one-row list, so the
  // tab is hidden from them entirely. `ClientTeamPage` also self-guards.
  const teamVisible = !me.system_admin && isLeadership(me);

  const tabs: ReadonlyArray<TabDef> = [
    { slug: 'overview', label: 'نظرة عامة', visible: true },
    { slug: 'onboarding', label: 'التأهيل', visible: onboardingVis.visible },
    {
      slug: 'strategy',
      label: 'الاستراتيجية',
      visible: strategyVis.visible,
      previewOnly: strategyVis.previewOnly,
    },
    {
      slug: 'content',
      label: 'المحتوى',
      visible: contentVis.visible,
      previewOnly: contentVis.previewOnly,
    },
    {
      slug: 'publishing',
      label: 'النشر',
      visible: publishingVis.visible,
      previewOnly: publishingVis.previewOnly,
    },
    {
      slug: 'paid-media',
      label: 'الإعلانات المدفوعة',
      visible: paidMediaVis.visible,
      previewOnly: paidMediaVis.previewOnly,
    },
    {
      slug: 'creative',
      label: 'مكتبة الإبداع',
      visible: creativeVis.visible,
      previewOnly: creativeVis.previewOnly,
    },
    {
      slug: 'creative-intelligence',
      label: 'ذكاء الإبداع',
      visible: creativeIntelligenceVis.visible,
    },
    {
      slug: 'influencers',
      label: 'المؤثرون',
      visible: influencersVis.visible,
      previewOnly: influencersVis.previewOnly,
    },
    { slug: 'reports', label: 'التقارير', visible: reportsVis.visible },
    { slug: 'services', label: 'الخدمات', visible: servicesVis.visible },
    { slug: 'contract', label: 'العقد', visible: contractVis.visible },
    { slug: 'team', label: 'الفريق', visible: teamVisible },
  ];

  const outletContext: ClientWorkspaceOutletContext = useMemo(
    () => ({
      clientId,
      capabilityStatus: capabilityState.status,
      hasActivePackage,
      capabilities,
      isAgencyActor,
      activeAssignment:
        capabilityState.status === 'ready' ? capabilityState.activeAssignment : null,
      refreshPackageAssignments,
      assignmentLookupConfirmed:
        capabilityState.status === 'ready' ? capabilityState.assignmentLookupConfirmed : false,
    }),
    [
      clientId,
      capabilityState,
      capabilities,
      hasActivePackage,
      isAgencyActor,
      refreshPackageAssignments,
    ],
  );

  return (
    <div className="stack-md">
      <div className="page-head">
        <div>
          <h1 className="h-page">
            {client?.name ?? 'العميل'}{' '}
            <span className="serif italic" style={{ color: 'var(--tangerine)', fontSize: '0.6em' }}>
              · workspace
            </span>
          </h1>
          {client ? (
            <p className="sub">
              الحالة: <strong>{client.status}</strong> · معرّف: <code>{clientId}</code>
            </p>
          ) : null}
        </div>
      </div>
      <div className="tabs" role="tablist">
        {tabs
          .filter((t) => t.visible)
          .map((t) => (
            <NavLink
              key={t.slug}
              to={`/app/clients/${clientId}/${t.slug}`}
              className={({ isActive }) =>
                `tab${isActive ? ' active' : ''}${t.previewOnly ? ' tab-preview' : ''}`
              }
              role="tab"
              title={
                t.previewOnly
                  ? 'معاينة الوكالة — هذه التابة غير مفعّلة لباقة هذا العميل.'
                  : undefined
              }
            >
              {t.label}
              {t.previewOnly ? <span className="tab-preview-pill">معاينة</span> : null}
            </NavLink>
          ))}
      </div>
      <div style={{ paddingTop: 8 }}>
        <CapabilityRouteGuard
          clientId={clientId}
          capabilityState={capabilityState}
          capabilities={capabilities}
          isAgencyActor={isAgencyActor}
          outletContext={outletContext}
        />
      </div>
    </div>
  );
}

/**
 * Guards the routed outlet: while the assignment query is still in
 * flight on a capability-gated route, render a skeleton instead of
 * letting the page paint. After the query resolves, if the package
 * doesn't unlock the route AND the actor is not an agency previewer,
 * render a locked-state card (the page still rendered would be
 * misleading — the data calls would either return empty or RLS-block
 * inconsistently). Foundation routes always pass through.
 */
function CapabilityRouteGuard({
  clientId,
  capabilityState,
  capabilities,
  isAgencyActor,
  outletContext,
}: {
  readonly clientId: string;
  readonly capabilityState: CapabilityState;
  readonly capabilities: ReadonlySet<SystemCapabilityKey>;
  readonly isAgencyActor: boolean;
  readonly outletContext: ClientWorkspaceOutletContext;
}) {
  const location = useLocation();
  const slug = currentTabSlug(location.pathname, clientId);
  const capKey = slug ? CAPABILITY_GATED_ROUTES[slug] : undefined;

  // Foundation routes / unknown routes — pass through.
  if (!capKey) {
    return <Outlet context={outletContext} />;
  }
  if (capabilityState.status === 'loading') {
    return <LoadingState title="جاري التحقق من قدرات الباقة…" />;
  }
  if (capabilityState.status === 'error') {
    // Surface the layout-level error rather than swallow it; the page
    // itself can't recover without the capability set.
    return <Outlet context={outletContext} />;
  }
  if (capabilities.has(capKey)) {
    return <Outlet context={outletContext} />;
  }
  if (isAgencyActor) {
    // Agency preview: render the page but mark it.
    return (
      <div className="stack-md">
        <div className="card package-banner package-banner-empty" role="status">
          <div>
            <strong>معاينة الوكالة</strong>
            <p className="muted" style={{ margin: '4px 0 0' }}>
              هذه التابة غير مفعّلة لباقة هذا العميل. تظهر لك بصفة وكالة. العميل لا يرى محتوى هذه
              التابة.
            </p>
          </div>
        </div>
        <Outlet context={outletContext} />
      </div>
    );
  }
  return <CapabilityLockedState clientId={clientId} capKey={capKey} />;
}

function currentTabSlug(pathname: string, clientId: string): string | null {
  // /app/clients/<id>/<slug>(/...)?
  const prefix = `/app/clients/${clientId}/`;
  if (!pathname.startsWith(prefix)) return null;
  const tail = pathname.slice(prefix.length);
  const slash = tail.indexOf('/');
  return (slash === -1 ? tail : tail.slice(0, slash)) || null;
}

function CapabilityLockedState({
  clientId,
  capKey,
}: {
  readonly clientId: string;
  readonly capKey: SystemCapabilityKey;
}) {
  return (
    <div className="card package-banner package-banner-empty" role="status">
      <div>
        <strong>هذه التابة غير مفعّلة لباقة هذا العميل</strong>
        <p className="muted" style={{ margin: '4px 0 0' }}>
          القدرة المطلوبة: <strong>{capabilityLabelAr(capKey)}</strong>. تواصل مع الوكالة لتفعيل
          باقة تشمل هذه القدرة، أو ارجع إلى نظرة عامة.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Link
          to={`/app/clients/${clientId}/services`}
          className="btn-text"
          style={{ padding: '8px 12px' }}
        >
          تفاصيل الباقة ←
        </Link>
        <Link
          to={`/app/clients/${clientId}/overview`}
          className="auth-cta"
          style={{ width: 'auto' }}
        >
          العودة إلى نظرة عامة
        </Link>
      </div>
    </div>
  );
}

/**
 * Active capabilities for an assignment. Prefers the snapshotted set
 * (the contractual truth at assignment time) and unions with the live
 * set so a tab unlocked by a package edit since the snapshot still
 * shows up. The wire arrays may legitimately be empty on older rows.
 */
function collectCapabilities(
  assignment: ClientPackageAssignmentRow,
): ReadonlySet<SystemCapabilityKey> {
  const out = new Set<SystemCapabilityKey>();
  for (const k of assignment.capability_keys_snapshot) out.add(k);
  for (const k of assignment.capability_keys) out.add(k);
  return out;
}
